import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ErrorBanner, LangToggle, Logo, Notice, Spinner } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";

function Shell({ title, subtitle, children }) {
  return (
    <div className="grain relative flex min-h-screen flex-col overflow-hidden bg-[#070709]">
      <div className="aurora opacity-40" />
      <div className="relative flex items-center justify-between p-6"><Link to="/"><Logo /></Link><LangToggle /></div>
      <div className="relative flex flex-1 items-center justify-center px-4 pb-16">
        <div className="rise w-full max-w-md lux-border rounded-3xl p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] sm:p-10">
          <h1 className="text-2xl font-semibold text-white" data-testid="auth-title">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-zinc-400">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, ...rest }) {
  return (
    <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} className="bg-[#111017]" {...rest} /></div>
  );
}

function GoogleButton() {
  const { t } = useT();
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return <Button type="button" variant="outline" className="w-full border-white/15 bg-transparent hover:bg-white/5 hover:text-white" onClick={go} data-testid="google-signin">{t("auth.google")}</Button>;
}

const Or = () => { const { t } = useT(); return <div className="my-5 flex items-center gap-3 text-xs text-zinc-500"><span className="h-px flex-1 bg-white/10" />{t("auth.or")}<span className="h-px flex-1 bg-white/10" /></div>; };

export function Login() {
  const { t } = useT();
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (user) return <Navigate to="/app" replace />;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { const { data } = await api.post("/auth/login", form); setUser(data); nav("/app"); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title={t("auth.signin")}>
      <GoogleButton /><Or />
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <ErrorBanner error={error} testId="login-error" />
        <Field id="email" label={t("auth.email")} type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" />
        <Field id="password" label={t("auth.password")} type="password" autoComplete="current-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" />
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">{busy ? "…" : t("auth.signin")}</Button>
      </form>
      <div className="mt-5 flex justify-between text-sm">
        <Link to="/forgot-password" className="text-zinc-400 hover:text-white" data-testid="link-forgot">{t("auth.forgot")}</Link>
        <Link to="/register" className="text-purple-300 hover:text-purple-200" data-testid="link-register">{t("auth.signup")}</Link>
      </div>
    </Shell>
  );
}

export function Register() {
  const { t } = useT();
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", workspace_name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (user) return <Navigate to="/app" replace />;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const { data } = await api.post("/auth/register", { ...form, workspace_name: form.workspace_name || null, lang: localStorage.getItem("cario_lang") || "en" });
      setUser(data); toast.success("Account created. Check your email to verify it."); nav("/app");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title={t("auth.signup")} subtitle="Transactional and permission-based email only.">
      <GoogleButton /><Or />
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <ErrorBanner error={error} testId="register-error" />
        <Field id="name" label={t("auth.name")} required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" />
        <Field id="remail" label={t("auth.email")} type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
        <Field id="rpass" label={`${t("auth.password")} (min. 10)`} type="password" autoComplete="new-password" minLength={10} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
        <Field id="wsn" label={t("auth.workspace")} maxLength={80} value={form.workspace_name} onChange={(e) => setForm({ ...form, workspace_name: e.target.value })} data-testid="register-workspace" />
        <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit">{busy ? "…" : t("auth.signup")}</Button>
      </form>
      <p className="mt-5 text-sm text-zinc-400">{t("auth.haveaccount")} <Link to="/login" className="text-purple-300">{t("auth.signin")}</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ busy: false, error: null, done: null });
  const submit = async (e) => {
    e.preventDefault(); setState({ busy: true, error: null, done: null });
    try { const { data } = await api.post("/auth/forgot-password", { email }); setState({ busy: false, error: null, done: data.message }); }
    catch (err) { setState({ busy: false, error: errMsg(err), done: null }); }
  };
  return (
    <Shell title={t("auth.reset")}>
      {state.done ? <Notice tone="ok" testId="forgot-success">{state.done}</Notice> : (
        <form onSubmit={submit} className="space-y-4">
          <ErrorBanner error={state.error} />
          <Field id="femail" label={t("auth.email")} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          <Button type="submit" className="w-full" disabled={state.busy} data-testid="forgot-submit">{t("auth.sendreset")}</Button>
        </form>
      )}
      <p className="mt-5 text-sm"><Link to="/login" className="text-purple-300">{t("auth.signin")}</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const { t } = useT();
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [state, setState] = useState({ busy: false, error: null, done: false });
  const submit = async (e) => {
    e.preventDefault(); setState({ busy: true, error: null, done: false });
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", password }); setState({ busy: false, error: null, done: true }); }
    catch (err) { setState({ busy: false, error: errMsg(err), done: false }); }
  };
  return (
    <Shell title={t("auth.reset")}>
      {state.done ? <Notice tone="ok" title="Password updated" testId="reset-success"><Link to="/login" className="underline">{t("auth.signin")}</Link></Notice> : (
        <form onSubmit={submit} className="space-y-4">
          <ErrorBanner error={state.error} testId="reset-error" />
          <Field id="np" label={t("auth.newpassword")} type="password" minLength={10} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" />
          <Button type="submit" className="w-full" disabled={state.busy} data-testid="reset-submit">{t("common.save")}</Button>
        </form>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const { reload } = useAuth();
  const [state, setState] = useState({ loading: true, error: null });
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return; ran.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(() => { setState({ loading: false, error: null }); reload(); })
      .catch((e) => setState({ loading: false, error: errMsg(e) }));
  }, [params, reload]);
  return (
    <Shell title="Email verification">
      {state.loading && <Spinner />}
      {!state.loading && state.error && <ErrorBanner error={state.error} testId="verify-error" />}
      {!state.loading && !state.error && <Notice tone="ok" title="Your email is verified" testId="verify-success"><Link to="/app" className="underline">Open dashboard</Link></Notice>}
    </Shell>
  );
}

export function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token") || undefined;
  const m = params.get("m") || undefined;
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ hint: null, needsEmail: false, error: null, done: false, busy: false });
  useEffect(() => {
    api.get("/public/unsubscribe/check", { params: { token, m } })
      .then(({ data }) => setState((s) => ({ ...s, hint: data.email_hint, needsEmail: !!data.needs_email })))
      .catch((e) => setState((s) => ({ ...s, error: errMsg(e) })));
  }, [token, m]);
  const confirm = async (e) => {
    e?.preventDefault();
    setState((s) => ({ ...s, busy: true, error: null }));
    try { await api.post("/public/unsubscribe", null, { params: { token, m, email: m ? email : undefined } }); setState((s) => ({ ...s, done: true, busy: false })); }
    catch (err) { setState((s) => ({ ...s, error: errMsg(err), busy: false })); }
  };
  return (
    <Shell title="Unsubscribe">
      {state.error && <div className="mb-4"><ErrorBanner error={state.error} /></div>}
      {state.done ? <Notice tone="ok" title="You are unsubscribed" testId="unsubscribe-success">You will no longer receive emails from this sender.</Notice>
        : state.needsEmail ? (
          <form onSubmit={confirm} className="space-y-4">
            <p className="text-sm text-zinc-300">This email went to several people. Enter the address you want to unsubscribe.</p>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="unsubscribe-email-input" />
            <Button type="submit" disabled={state.busy} data-testid="unsubscribe-confirm">Unsubscribe</Button>
          </form>)
        : state.hint && (<div className="space-y-4"><p className="text-sm text-zinc-300">Stop receiving emails at <b>{state.hint}</b> from this sender?</p>
          <Button onClick={confirm} disabled={state.busy} data-testid="unsubscribe-confirm">Unsubscribe</Button></div>)}
    </Shell>
  );
}

export function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return; done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data); window.history.replaceState(null, "", "/app"); nav("/app", { replace: true }); })
      .catch((e) => { toast.error(errMsg(e)); nav("/login", { replace: true }); });
  }, [location.hash, nav, setUser]);
  return null;
}
